import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import "./progress-section.css";

type CollageCard = {
  /** Photo aspect ratio (width / height). Mixed across cards so the columns never line up. */
  ratio: string;
  image: string;
  alt: string;
  position?: string;
  date?: string;
  title: string;
  description?: string;
  links?: Array<{ to: string; label: string; external?: boolean }>;
};

const columns: CollageCard[][] = [
  [
    {
      ratio: "4 / 5",
      image: "/dibs_assets/about/impact-irl-booth.jpg",
      alt: "The Dibs team standing beside the Dibs for Youths booth at Impact, IRL",
      position: "center 30%",
      date: "31 December 2026",
      title: "Dibs for Youth at Impact, IRL",
      description:
        "Dibs for Youth gives government platforms a shared memory, so every youth gets personalised support without repeating their story. We were at Impact, IRL to showcase it and talk to more youths.",
      links: [{ to: "/foryouth", label: "Join the Dibs for Youth waitlist" }],
    },
  ],
  [
    {
      ratio: "4 / 3",
      image: "/dibs_assets/about/sandbox-wall.jpg",
      alt: "Two Dibs founders pointing at the Dibs logo on the Sandbox Agile wall",
      position: "75% center",
      date: "13 May 2026",
      title: "Winning the Agile Startup Fund",
      description:
        "Dibs was awarded the Ngee Ann Polytechnic Agile Startup Fund, and we joined the Agile community at Sandbox. It's early backing for the problem we're solving, and fuel to keep building.",
      links: [{ to: "https://www.agile.edu.sg/community.html", label: "As seen on Agile", external: true }],
    },
  ],
  [
    {
      ratio: "3 / 2",
      image: "/dibs_assets/about/team-group.jpg",
      alt: "The Dibs team gathered together, giving thumbs up",
      position: "center 40%",
      date: "18 January 2025",
      title: "Our first Dibs Day",
      description:
        "Dibs Day is a day where we get together to celebrate the team. Relationships matter as much inside the team as they do with customers, and Dibs is built on that belief.",
      links: [{ to: "/about", label: "Learn more about us" }],
    },
  ],
];

export default function ProgressSection() {
  return (
    <section className="dh-section dh-progress" aria-labelledby="dh-progress-title">
      <div className="dh-container">
        <div className="dh-section-heading">
          <div className="dh-kicker">Our story</div>
          <h2 id="dh-progress-title">We built a shared memory. Here’s how it’s going.</h2>
          <p>A look at the moments, big and small, that shaped Dibs.</p>
        </div>

        <div className="dh-collage">
          {columns.map((cards, column) => (
            <div className="dh-collage-column" key={column}>
              {cards.map(({ ratio, image, alt, position, date, title, description, links }) => (
                <article className="dh-collage-card" key={title}>
                  <div className="dh-collage-media" style={{ aspectRatio: ratio }}>
                    <img src={image} alt={alt} style={{ objectPosition: position }} loading="lazy" decoding="async" />
                  </div>
                  <div className="dh-collage-body">
                    {date && <time className="dh-collage-date">{date}</time>}
                    <h3>{title}</h3>
                    {description && <p>{description}</p>}
                    {links?.map(({ to, label, external }) =>
                      external ? (
                        <a className="dh-collage-link" href={to} target="_blank" rel="noopener noreferrer" key={to}>
                          {label} <ArrowRight aria-hidden />
                        </a>
                      ) : (
                        <Link className="dh-collage-link" to={to} key={to}>
                          {label} <ArrowRight aria-hidden />
                        </Link>
                      ),
                    )}
                  </div>
                </article>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
